// The only place this module talks to the funnel stats API (Precision Funnel
// C8, repo `funnel-stats`, served at `/v1/funnels/*`). It credits Shopify
// orders to a lander version and a traffic source once a day.
//
// The portal is not a backend (I-4): every count, every revenue figure and
// every rate here is the service's. This module only formats.

import { cutQuery, type Enveloped, type FunnelCut, type WindowDays } from "./api";

/** The figures the service sends for any cut. Rates are fractions; `null` when the denominator is 0. */
export type StatsFigures = {
  visitors: number;
  views: number;
  /** Sign-ups. */
  leads: number;
  orders: number;
  revenue_gbp: number;
  /** Sign-ups per visitor. */
  lead_rate: number | null;
  /** Orders per sign-up. */
  order_rate: number | null;
  revenue_per_visitor: number | null;
};

export type StatsWindow = { days: number; from: string; to: string };

/** `variant` is `null` for orders whose sign-up recorded no version. */
export type VariantOrders = StatsFigures & { variant: string | null };

/**
 * `source` is `null` when the lander could not name where the visit came from. The every-funnel
 * answer adds the in-app checkout orders for the same source key (C8 R3, funnel-stats spec 06).
 */
export type SourceOrders = StatsFigures & { source: string | null; app_orders?: number; app_revenue_gbp?: number };

/** In-app checkout orders for one app source and door (C8 R3). */
export type AppSegment = { source: string; door: string; orders: number; revenue_gbp: number };

export type FunnelPagesStats = { slug: string; window: StatsWindow; rows: VariantOrders[]; totals: StatsFigures };

/** One lander page (language x version). Either key is `null` for orders whose sign-up named no page. */
export type PageFigures = StatsFigures & { locale: string | null; variant: string | null };

export type FunnelPageFiguresStats = {
  slug: string;
  /** `null` = every source. */
  source: string | null;
  window: StatsWindow;
  rows: PageFigures[];
  totals: StatsFigures;
  /** Orders whose sign-up names no page. */
  unmatched: { orders: number; revenue_gbp: number };
};

/** `slug` is `null` on the every-funnel answer (`GET /v1/funnels/sources`, GATE-379 FN-2). */
export type FunnelSourcesStats = {
  slug: string | null;
  window: StatsWindow;
  rows: SourceOrders[];
  totals: StatsFigures & { app_orders?: number; app_revenue_gbp?: number };
  /** Every-funnel answer only: in-app orders by app source and door. */
  app?: { segments: AppSegment[]; totals: { orders: number; revenue_gbp: number } };
};

export type FunnelStatsList = {
  window: StatsWindow;
  funnels: (StatsFigures & { slug: string })[];
  /** Orders in the window that no funnel can claim. */
  unattributed: { orders: number; revenue_gbp: number };
};

/** The cut an Outcomes table is keyed by (C9 R4). */
export type OutcomeCut = "variant" | "source" | "goal" | "product";

/** First-order cohort windows the Outcomes tab offers, in days. */
export const COHORTS = [90, 180, 365] as const;
export type CohortDays = (typeof COHORTS)[number];

/** Lifetime figures for customers whose first order fell in the cohort window. `null` = not computable. */
export type OutcomeFigures = {
  customers: number | null;
  repeat_rate: number | null;
  orders_per_customer: number | null;
  revenue_gbp: number | null;
  /** Revenue per customer, lifetime to the latest snapshot. */
  ltv_gbp: number | null;
  contribution_gbp: number | null;
  contribution_per_customer: number | null;
  /** Customers at least 90 days past their first order. */
  matured_90d: number | null;
  /** Mean first-90-day revenue over `matured_90d`; `null` when none has matured. */
  ltv_90d_gbp: number | null;
};

/** One row of a cut. The key is the cut's name; `suppressed` rows carry their label and `null` figures. */
export type OutcomeRow = OutcomeFigures & {
  variant?: string | null;
  source?: string | null;
  goal?: string | null;
  product?: string | null;
  suppressed: boolean;
};

export type FunnelOutcomesStats = {
  slug: string;
  by: OutcomeCut;
  window: StatsWindow;
  rows: OutcomeRow[];
  totals: OutcomeFigures & { suppressed_rows: number };
};

// Precision Funnel C12 (AXIS onboarding drop-off). The four doors of the Phase 1 scope canvas.
export type Door = "all" | "a_pack" | "b_app" | "cs" | "existing";
export const DOORS: { value: Door; label: string }[] = [
  { value: "all", label: "All doors" },
  { value: "a_pack", label: "QR Scan" },
  { value: "b_app", label: "App Front Door" },
  { value: "cs", label: "CS (Typeform)" },
  { value: "existing", label: "Existing User" },
];

/** One onboarding screen, or a group of alternative screens (`is_group`). Every count is real, however small (bug 4070402f-8187). */
export type OnboardingStep = {
  route: string;
  label: string;
  is_group: boolean;
  group_key: string | null;
  position: number;
  doors: string[];
  door_tag: string | null;
  asked_tag: string | null;
  goal_key: string | null;
  viewed: number | null;
  completed: number | null;
  drop_rate: number | null;
  median_seconds: number | null;
  time_people: number | null;
};

export type BiggestDrop = { route: string; label: string; lost: number; viewed: number; drop_rate: number };

export type OnboardingTotals = {
  started: number;
  finished: number | null;
  finish_rate: number | null;
  biggest_drop: BiggestDrop | null;
  signed_up_people: number;
  not_signed_up_people: number;
  no_door_people: number;
};

export type FunnelOnboardingStats = {
  slug: string;
  door: Door;
  product: string;
  goal: string;
  sub_goal: string;
  window: { from: string; to: string };
  steps: OnboardingStep[];
  totals: OnboardingTotals;
  unknown_routes: { route: string; people: number }[];
  screens_named: boolean;
  /** `sub_goals` is keyed by goal (funnel-stats spec 02, GATE-360 R-4): a goal with no kinds is absent. */
  choices: { products: string[]; goals: string[]; sub_goals: Record<string, string[]> };
  choices_as_of: string | null;
  data_as_of: string | null;
};

export type OnboardingQuery = { door: Door; product: string; goal: string; sub_goal: string; from: string; to: string };

// KET-8425: the Funnels App launch tab (funnel-stats spec 04). Every count is the real number, however
// small (DEC-865): this internal tab has no small-group suppression. A rate is `null` when its base is 0.
export type AppLaunchStep = {
  flow: string;
  stage: string;
  step_id: string;
  /** The app's own screen index (all doors and branches); null for a catalog screen nobody reached. */
  step_index: number | null;
  /** 1..N in the door's catalog order (bug 4070402f-8145); null for a screen the catalog does not know. */
  position?: number | null;
  label?: string | null;
  in_catalog?: boolean;
  people_reached: number | null;
  people_completed: number | null;
  people_in_progress: number | null;
  people_lost: number | null;
  loss_rate: number | null;
};

export type AppLaunchMilestones = {
  people: number | null;
  app_entry: number | null;
  onboarding_started: number | null;
  step_viewed: number | null;
  sign_up_started: number | null;
  sign_up: number | null;
  onboarding_complete: number | null;
};

export type AppLaunchOrders = {
  people: number | null;
  dose_plan_created: number | null;
  product_checked: number | null;
  checkout_opened: number | null;
  checkout_claimed: number | null;
  order_placed: number | null;
  orders_placed: number | null;
  order_rate: number | null;
};

export type AppLaunchEmail = {
  email_type: string;
  email_key: string;
  has_desired_action: boolean;
  sent: number | null;
  delivered: number | null;
  opened: number | null;
  clicked: number | null;
  bounced: number | null;
  complained: number | null;
  unsubscribed: number | null;
  returned_to_app: number | null;
  desired_action: number | null;
  open_rate: number | null;
  click_rate: number | null;
  return_rate: number | null;
  desired_action_rate: number | null;
};

/** One of Damien's launch KPIs (funnel-stats specs 07 and 08), in funnel order. */
export type AppLaunchKpi = {
  key: "entered_onboarding" | "results_given" | "account_created" | "order_made" | "precision_home";
  label: string;
  people: number | null;
  /** People ÷ Entered onboarding. Absent until funnel-stats spec 08 is deployed. */
  rate_from_total?: number | null;
  /** People ÷ the row this step depends on (previous_key). */
  rate_from_previous: number | null;
  /** The row the previous-step rate is measured against (spec 08); null on the first row. */
  previous_key?: AppLaunchKpi["key"] | null;
};

export type FunnelAppLaunchStats = {
  slug: string;
  window: { from: string; to: string };
  step_loss: AppLaunchStep[];
  biggest_loss: { flow: string; step_id: string; people_lost: number; loss_rate: number } | null;
  /** Absent until funnel-stats spec 07 is deployed. */
  launch_kpis?: AppLaunchKpi[];
  milestones: AppLaunchMilestones;
  orders: AppLaunchOrders;
  email: AppLaunchEmail[];
};

export type AppLaunchQuery = { from: string; to: string };

// Conversion Journey (C11 thin, funnel-stats spec 09). These are transport
// shapes only: the portal formats the values but never derives funnel figures.
export type JourneyPath = "pre" | "post";
export type JourneyCutKey = "source" | "door" | "goal" | "device" | "language";

export type JourneyKpi = {
  key: string;
  label: string;
  people: number | null;
  base: number | null;
  rate: number | null;
};

export type JourneyMilestone = {
  key: string;
  label: string;
  people: number | null;
  rate_from_previous: number | null;
  lost: number | null;
};

export type JourneyButton = { button: string; people: number; share_of_viewed: number | null };

export type JourneyStep = {
  flow: string;
  stage: string;
  step_id: string;
  step_index: number | null;
  position: number | null;
  label: string | null;
  in_catalog: boolean;
  viewed: number | null;
  completed: number | null;
  lost: number | null;
  in_progress: number | null;
  loss_rate: number | null;
  median_seconds: number | null;
  time_people: number | null;
  last_seen_at: string | null;
  records_choice: boolean;
  buttons: JourneyButton[];
  no_button: { people: number; share_of_viewed: number | null } | null;
};

export type JourneyQuery = {
  from: string;
  to: string;
  path: JourneyPath;
  funnel: string;
  source: string | null;
  door: string | null;
  goal: string | null;
  device: string | null;
  language: string | null;
};

export type FunnelJourneyStats = {
  slug: string;
  window: { from: string; to: string };
  path: JourneyPath;
  funnel: { key: string; label: string; mapped: boolean };
  funnels: Array<{ key: string; label: string; path: JourneyPath; mapped: boolean }>;
  cut: Record<JourneyCutKey, string | null>;
  people: number | null;
  low_data: boolean;
  kpis: JourneyKpi[];
  milestones: JourneyMilestone[];
  steps: JourneyStep[];
  biggest_loss: { step_id: string; lost: number; loss_rate: number } | null;
  signins: number | null;
  needs_data: Array<{ key: string; reason: string }>;
  data_as_of: string | null;
};

export type JourneyStepStats = {
  step_id: string;
  label: string;
  by: JourneyCutKey;
  all: {
    viewed: number | null;
    completed: number | null;
    lost: number | null;
    in_progress: number | null;
    loss_rate: number | null;
    median_seconds: number | null;
  };
  rows: Array<{
    value: string;
    viewed: number | null;
    completed: number | null;
    lost: number | null;
    in_progress: number | null;
    loss_rate: number | null;
  }>;
  buttons: Array<{ button: string; all: number; by: Record<string, number> }>;
  no_button: { all: number; by: Record<string, number> } | null;
  low_data: boolean;
};

export type JourneySegments = {
  window: { from: string; to: string };
  path: JourneyPath;
  segments: Record<JourneyCutKey, Array<{ value: string; people: number }>>;
};

export type OverviewFunnelKey = "all" | "precision-main" | "client-solutions" | "precision-carbs" | "precision-qr";
export type OverviewNeeds = { reason: string } | null;
export type OverviewTile = {
  key: "leads" | "new_app_users" | "lead_to_app" | "results_given" | "order_made" | "active_users" | "stickiness" | "orders_per_app_user";
  label: string;
  value: number | null;
  people: number | null;
  base: number | null;
  needs_data: OverviewNeeds;
  parts?: { landing_visitors: number | null; cs_intake_starts: number | null };
  from_qr_service?: boolean;
  detail?: {
    avg_dau: number | null;
    mau: number | null;
    wau: number | null;
    wau_mau: number | null;
    days_covered: number;
    partial: boolean;
    checkins: { value: number | null; needs_data: OverviewNeeds };
  };
};
export type OverviewCell = {
  value: number | null;
  people?: number | null;
  base?: number | null;
  needs_data: OverviewNeeds;
  not_applicable?: boolean;
};
export type OverviewFunnelRow = {
  /** `other` = app users no funnel claims (PDEC-103 OV-OTHER-1 A), so the rows sum to the stage totals. */
  key: Exclude<OverviewFunnelKey, "all"> | "other";
  other?: boolean;
  label: string;
  stage: "pre" | "post";
  mapped: boolean;
  leads: OverviewCell;
  new_app_users: OverviewCell;
  lead_to_app: OverviewCell;
  results_given: OverviewCell;
  order_made: OverviewCell;
  active_users: OverviewCell;
  stickiness: OverviewCell;
  orders_per_app_user: OverviewCell;
  leads_trend: { week: string; value: number }[] | null;
};
export type FunnelOverviewStats = {
  slug: string;
  window: { from: string; to: string };
  funnel: OverviewFunnelKey;
  stages: { pre: { tiles: OverviewTile[] }; post: { tiles: OverviewTile[] } };
  funnels: OverviewFunnelRow[];
  data_as_of: string | null;
};
export type OverviewQuery = { from: string; to: string; funnel: OverviewFunnelKey };

// Same-origin via the Pages Function proxy (functions/funnels/[[path]].ts).
const BASE = import.meta.env.VITE_FUNNEL_STATS_API_BASE ?? "/funnels/v1";

export class FunnelStatsError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Funnel stats API ${status}`);
  }
}

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    throw new FunnelStatsError(response.status, body);
  }
  return (await response.json()) as Enveloped<T>;
}

export const getFunnelStats = (days: WindowDays) => get<FunnelStatsList>(`?days=${days}`);

/** Optional `source` / `locale` cuts; `null` = every source / language. */
export type StatsCut = FunnelCut;

export const getFunnelPages = (slug: string, days: WindowDays, cut?: StatsCut) =>
  get<FunnelPagesStats>(`/${encodeURIComponent(slug)}/pages?days=${days}${cutQuery(cut)}`);

export const getFunnelSources = (slug: string, days: WindowDays) =>
  get<FunnelSourcesStats>(`/${encodeURIComponent(slug)}/sources?days=${days}`);

/** Every funnel's sources together: the Sources tab's All funnels view (GATE-379 FN-2). */
export const getFunnelSourcesAll = (days: WindowDays) => get<FunnelSourcesStats>(`/sources?days=${days}`);

/** `source` `null` asks for every source. */
export const getFunnelPageFigures = (slug: string, days: WindowDays, source: string | null) =>
  get<FunnelPageFiguresStats>(`/${encodeURIComponent(slug)}/page-figures?days=${days}${cutQuery({ source, locale: null })}`);

export const getFunnelOutcomes = (slug: string, by: OutcomeCut, cohort: CohortDays | 30) =>
  get<FunnelOutcomesStats>(`/${encodeURIComponent(slug)}/outcomes?by=${by}&days=${cohort}`);

export const getFunnelOnboarding = (slug: string, q: OnboardingQuery) => {
  const params = new URLSearchParams({ door: q.door, product: q.product, goal: q.goal, sub_goal: q.sub_goal, from: q.from, to: q.to });
  return get<FunnelOnboardingStats>(`/${encodeURIComponent(slug)}/onboarding?${params.toString()}`);
};

export const statsOnboardingKey = (slug: string, q: OnboardingQuery) =>
  ["funnel_stats", "onboarding", slug, q.door, q.product, q.goal, q.sub_goal, q.from, q.to] as const;

export const getFunnelAppLaunch = (slug: string, q: AppLaunchQuery) =>
  get<FunnelAppLaunchStats>(`/${encodeURIComponent(slug)}/app-launch?from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}`);

export const statsAppLaunchKey = (slug: string, q: AppLaunchQuery) => ["funnel_stats", "app_launch", slug, q.from, q.to] as const;

function journeyParams(q: JourneyQuery): URLSearchParams {
  const params = new URLSearchParams({ from: q.from, to: q.to, path: q.path, funnel: q.funnel });
  for (const key of ["source", "door", "goal", "device", "language"] as const) {
    if (q[key]) params.set(key, q[key]);
  }
  return params;
}

export const getFunnelJourney = (slug: string, q: JourneyQuery, fresh = false) => {
  const params = journeyParams(q);
  if (fresh) params.set("fresh", "1");
  return get<FunnelJourneyStats>(`/${encodeURIComponent(slug)}/journey?${params.toString()}`);
};

export const getFunnelJourneyStep = (slug: string, q: JourneyQuery, stepId: string, by: JourneyCutKey) => {
  const params = journeyParams(q);
  params.set("step_id", stepId);
  params.set("by", by);
  return get<JourneyStepStats>(`/${encodeURIComponent(slug)}/journey/step?${params.toString()}`);
};

export const getFunnelJourneySegments = (slug: string, q: Pick<JourneyQuery, "from" | "to" | "path">) => {
  const params = new URLSearchParams({ from: q.from, to: q.to, path: q.path });
  return get<JourneySegments>(`/${encodeURIComponent(slug)}/journey/segments?${params.toString()}`);
};

export const getFunnelOverview = (slug: string, q: OverviewQuery) =>
  get<FunnelOverviewStats>(
    `/${encodeURIComponent(slug)}/overview?from=${encodeURIComponent(q.from)}&to=${encodeURIComponent(q.to)}&funnel=${encodeURIComponent(q.funnel)}`,
  );

export const statsOverviewKey = (slug: string, q: OverviewQuery) =>
  ["funnel_stats", "overview", slug, q.from, q.to, q.funnel] as const;

export const statsJourneyKey = (slug: string, q: JourneyQuery) =>
  ["funnel_stats", "journey", slug, q.from, q.to, q.path, q.funnel, q.source ?? "*", q.door ?? "*", q.goal ?? "*", q.device ?? "*", q.language ?? "*"] as const;

export const statsJourneyStepKey = (slug: string, q: JourneyQuery, stepId: string, by: JourneyCutKey) =>
  ["funnel_stats", "journey", "step", slug, q.from, q.to, q.path, q.funnel, q.source ?? "*", q.door ?? "*", q.goal ?? "*", q.device ?? "*", q.language ?? "*", stepId, by] as const;

export const statsJourneySegmentsKey = (slug: string, q: Pick<JourneyQuery, "from" | "to" | "path">) =>
  ["funnel_stats", "journey", "segments", slug, q.from, q.to, q.path] as const;

/** Keys start with `funnel_stats` so they never collide with the Worker's `landing_pages` keys. */
export const statsListKey = (days: WindowDays) => ["funnel_stats", "funnels", days] as const;
export const statsPagesKey = (slug: string, days: WindowDays, cut?: StatsCut) =>
  ["funnel_stats", "pages", slug, days, cut?.source ?? "*", cut?.locale ?? "*"] as const;
export const statsSourcesKey = (slug: string, days: WindowDays) => ["funnel_stats", "sources", slug, days] as const;
export const statsPageFiguresKey = (slug: string, days: WindowDays, source: string | null) =>
  ["funnel_stats", "page_figures", slug, days, source ?? "*"] as const;

export const statsOutcomesKey = (slug: string, by: OutcomeCut, cohort: CohortDays | 30) =>
  ["funnel_stats", "outcomes", slug, by, cohort] as const;

/** One message per failure. `areaName` comes from the registry via the shell (I-5). */
export function statsMessageFor(error: unknown, areaName: string, subject = "Order figures"): string {
  if (!(error instanceof FunnelStatsError)) {
    return `${subject} could not be read, so none are shown. Try again shortly.`;
  }
  switch (error.status) {
    case 400:
      return "That time window is not one this view reports on.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 503:
      return `${subject} are not configured on the server, so none are shown.`;
    default:
      return `${subject} could not be read, so none are shown. Try again shortly.`;
  }
}
